import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  AuthSuccess,
  LoginRequest,
  LoginResponse,
  VerifyTwoFactorRequest,
} from '@/types/auth.types';
import { CURRENT_USER_QUERY_KEY } from '@/hooks/useCurrentUser.hook';
import { ROUTES } from '@/routes/route.config';
import { authApi } from '@/services/api.service';
import { authStorage } from '@/services/authStorage.service';
import { loginRequestSchema, twoFactorCodeSchema } from '@/validators/auth.validator';

type FormState = LoginRequest;
type FieldErrors = Partial<Record<keyof FormState, string>>;

/** Which half of the sign-in the user is looking at. */
export type LoginStep = 'password' | 'twoFactor';

interface UseLoginPageResult {
  step: LoginStep;
  email: string;
  password: string;
  code: string;
  codeError: string | null;
  errors: FieldErrors;
  submitError: string | null;
  isSubmitting: boolean;
  onChangeEmail: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangePassword: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeCode: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onSubmitCode: (event: FormEvent<HTMLFormElement>) => void;
  onBackToPassword: () => void;
}

const INITIAL_FORM: FormState = { email: '', password: '' };
const GENERIC_ERROR = 'Unable to log in. Please try again.';

/**
 * The axios layer flattens every failure to a plain `Error` carrying only `message`, so there is
 * no status or code to branch on here — read the message it already extracted.
 */
const messageOf = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : GENERIC_ERROR;

export const useLoginPage = (): UseLoginPageResult => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [step, setStep] = useState<LoginStep>('password');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);

  const completeSignIn = (data: AuthSuccess): void => {
    authStorage.setTokens({ token: data.token, refreshToken: data.refreshToken });
    queryClient.setQueryData(CURRENT_USER_QUERY_KEY, data.user);
    navigate(ROUTES.dashboard, { replace: true });
  };

  const loginMutation = useMutation<LoginResponse, unknown, LoginRequest>({
    mutationFn: (body) => authApi.login(body),
    onSuccess: (data) => {
      if ('twoFactorRequired' in data) {
        setChallengeId(data.challengeId);
        setStep('twoFactor');
        return;
      }
      completeSignIn(data);
    },
    onError: (error) => setSubmitError(messageOf(error)),
  });

  const verifyMutation = useMutation<AuthSuccess, unknown, VerifyTwoFactorRequest>({
    mutationFn: (body) => authApi.verifyTwoFactor(body),
    onSuccess: completeSignIn,
    onError: (error) => setSubmitError(messageOf(error)),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSubmitError(null);

    const parsed = loginRequestSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({ email: fieldErrors.email?.[0], password: fieldErrors.password?.[0] });
      return;
    }

    setErrors({});
    loginMutation.mutate(parsed.data);
  };

  const onSubmitCode = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSubmitError(null);

    const parsed = twoFactorCodeSchema.safeParse({ code });
    if (!parsed.success) {
      setCodeError(parsed.error.flatten().fieldErrors.code?.[0] ?? 'Enter the 6-digit code');
      return;
    }
    if (!challengeId) {
      setSubmitError(GENERIC_ERROR);
      return;
    }

    setCodeError(null);
    verifyMutation.mutate({ challengeId, code: parsed.data.code });
  };

  /** Back to step one. The challenge is abandoned, so a fresh code must be requested. */
  const onBackToPassword = (): void => {
    setStep('password');
    setChallengeId(null);
    setCode('');
    setCodeError(null);
    setSubmitError(null);
    setForm((prev) => ({ ...prev, password: '' }));
  };

  return {
    step,
    email: form.email,
    password: form.password,
    code,
    codeError,
    errors,
    submitError,
    isSubmitting: loginMutation.isPending || verifyMutation.isPending,
    onChangeEmail: (event) => setForm((prev) => ({ ...prev, email: event.target.value })),
    onChangePassword: (event) => setForm((prev) => ({ ...prev, password: event.target.value })),
    onChangeCode: (event) => {
      setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
      setCodeError(null);
    },
    onSubmit,
    onSubmitCode,
    onBackToPassword,
  };
};
